import isArray from 'lodash/isArray';

import transformMapboxStepZoomExpression from './transformMapboxStepZoomExpression';

type Font = string[] | mapboxgl.StyleFunction | mapboxgl.Expression | undefined;

export default function transformMapboxStyleFonts(
    style: mapboxgl.Style,
    transformValue: (input: string[]) => string[],
    transformExpression: (input: mapboxgl.Expression) => mapboxgl.Expression
): mapboxgl.Style {
    if (!style.layers) return style;

    function transformTextFontExpression(expression: any): any {
        if (!isArray(expression)) {
            return expression;
        } else if (expression[0] === 'format') {
            return expression.map((value, index) => {
                if (index === 0 || index % 2 === 1 || !value['text-font']) {
                    return value;
                } else {
                    return {
                        ...value,
                        'text-font': transformMapboxStepZoomExpression(
                            value['text-font'],
                            transformExpression
                        ),
                    };
                }
            });
        } else {
            return expression.map(transformTextFontExpression);
        }
    }

    return {
        ...style,
        layers: style.layers.map((layer) => {
            if (layer.type !== 'symbol' || !layer.layout) return layer;
            const prevFont = (layer.layout as mapboxgl.SymbolLayout)['text-font'] as Font;
            const prevTextField = (layer.layout as mapboxgl.SymbolLayout)['text-field'];

            if (!prevFont && !isArray(prevTextField)) return layer;

            let nextFont;
            if (prevFont) {
                if (isMapboxStyleFunction(prevFont)) {
                    nextFont = transformMapboxStepZoomExpression(prevFont, transformExpression);
                } else if (isArray(prevFont)) {
                    nextFont = transformValue(prevFont);
                } else if (prevFont.stops) {
                    nextFont = {
                        ...prevFont,
                        stops: prevFont.stops.map(([key, value]) => [key, transformValue(value)]),
                    };
                }
            }

            const nextTextField =
                isArray(prevTextField) && transformTextFontExpression(prevTextField);

            return {
                ...layer,
                layout: {
                    ...layer.layout,
                    ...(nextFont ? {'text-font': nextFont} : undefined),
                    ...(nextTextField ? {'text-field': nextTextField} : undefined),
                },
            } as mapboxgl.AnyLayer;
        }),
    };
}

// In most other cases, if a Mapbox style property is an array, we can assume it's an expression.
// `text-font`, however, can be passed a literal array of strings. We disambiguate by checking if
// any array elements are themselves an array, as would be required in almost any valid text-font
// expression.
function isMapboxStyleFunction(input: any): input is mapboxgl.Expression {
    return isArray(input) && input.some(isArray);
}
